// One fictional payment ties the independent checks together. Values are in
// USDC; the selected policy requires two signatures and a daily account cap.
// Share these values with the policy view so its rules match every scene.
export const SECURITY_WORKFLOW_EXAMPLE = {
  amount: '1,250',
  dailyCap: '5,000',
  overLimitAmount: '7,500',
  recipient: 'Design partner',
  policy: 'Vendor payments',
  requiredApprovals: 2,
  members: ['Alex', 'Sam', 'Jo'],
} as const;

type CreationDraft = {
  invalidField: 'recipient' | 'amount' | 'asset' | null;
  asset: 'USDC' | 'ETH';
  amount: string;
  recipient: string;
  title: string;
  detail: string;
};

const EXAMPLE = SECURITY_WORKFLOW_EXAMPLE;
const VALID_CREATION_DRAFT: CreationDraft = {
  invalidField: null,
  asset: 'USDC',
  amount: EXAMPLE.amount,
  recipient: EXAMPLE.recipient,
  title: 'All policy checks passed',
  detail: 'Recipient, amount, and asset verified',
};

const INVALID_CREATION_DRAFTS: readonly CreationDraft[] = [
  {
    ...VALID_CREATION_DRAFT,
    invalidField: 'recipient',
    recipient: 'Unapproved recipient',
    title: 'Recipient not allowed',
    detail: 'Only approved vendors can receive this payment.',
  },
  {
    ...VALID_CREATION_DRAFT,
    invalidField: 'amount',
    amount: EXAMPLE.overLimitAmount,
    title: 'Amount exceeds policy limit',
    detail: `${EXAMPLE.overLimitAmount} USDC exceeds the ${EXAMPLE.dailyCap} USDC daily limit.`,
  },
  {
    ...VALID_CREATION_DRAFT,
    invalidField: 'asset',
    asset: 'ETH',
    amount: '0.5',
    title: 'ETH is not allowed',
    detail: 'The selected policy only allows USDC.',
  },
];

export const SECURITY_BLOCKED_DRAFT_COUNT = INVALID_CREATION_DRAFTS.length;
// A ready frame follows the blocked drafts before initiation is confirmed.
export const SECURITY_CREATION_INITIATED_PHASE =
  SECURITY_BLOCKED_DRAFT_COUNT + 1;
export const SECURITY_CREATION_SCENE_COUNT =
  SECURITY_CREATION_INITIATED_PHASE + 1;

export function getSecurityCreationDraft(phase: number): CreationDraft {
  // Each scene replaces the previous invalid draft. The ready and initiated
  // frames share a valid draft, so no rejection remains after correction.
  return INVALID_CREATION_DRAFTS[phase] ?? VALID_CREATION_DRAFT;
}
